import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { stringifyDetail } from '../lib/utils';

const API_BASE = window.location.origin;

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() })
            });
            if (res.status === 429) {
                setError('Too many reset requests. Please try again later.');
                return;
            }
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(stringifyDetail(data.detail) || 'Something went wrong. Please try again.');
                return;
            }
            setSent(true);
        } catch {
            setError('Unable to connect. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4" data-testid="forgot-password-page">
            <div className="w-full max-w-md">
                <button
                    onClick={() => navigate('/login')}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
                    data-testid="back-to-login"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to login
                </button>

                <div className="bg-background rounded-2xl border p-8 shadow-sm">
                    {sent ? (
                        <div className="text-center" data-testid="reset-sent-confirmation">
                            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                            </div>
                            <h1 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Check your email
                            </h1>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                If an account exists for <strong>{email}</strong>, we've sent a password reset link. The link expires in 15 minutes.
                            </p>
                            <Button
                                variant="outline"
                                onClick={() => navigate('/login')}
                                className="w-full"
                                data-testid="return-to-login-btn"
                            >
                                Return to login
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Forgot Password
                                    </h1>
                                    <p className="text-xs text-muted-foreground">
                                        Enter your email to receive a reset link
                                    </p>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 mb-4" data-testid="forgot-error">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="reset-email" className="text-sm">Email address</Label>
                                    <Input
                                        id="reset-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        className="mt-1"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading || !email.trim()}
                                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Mail className="w-4 h-4 mr-2" />
                                    )}
                                    Send Reset Link
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
